import React, { useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { displayToast } from "../utils/displayToast";
import {
  createMasterTransaction,
  updateTransaction,
} from "../apis/transactionApi";
import "../styles/components/MasterTransaction.css";

const CUSTOMER_TYPES = ["customer", "vendor"];

const MasterTransaction = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSavingTransaction, setIsSavingTransaction] = useState(false);
  
  const isViewMode = location.state?.viewMode === true;

  const initialState = {
    type: "master",
    name: "",
    tran_code: "",
    is_auto_transaction_no: false,
    is_back_dated_entry_allowed: false,
    customer_type: "customer",
    is_pick_up_mandatory: false,
    head_view_fields: [],
  };

  const arrayFields = ["head_view_fields"];

  const [formData, setFormData] = useState(() => {
    const transaction = location.state?.transaction;

    if (!transaction) return initialState;

    const normalized = {
      ...initialState,
      ...transaction,
      customer_type: transaction.customer_type || "customer",
    };

    arrayFields.forEach((key) => {
      if (Array.isArray(transaction[key])) {
        normalized[key] = transaction[key].map((item) => {
          const storedFlexValues = JSON.parse(
            localStorage.getItem(`flex_values_${transaction.id}`) || "{}"
          );
          const fieldKey = `${item.field_name}_${item.display_name}`;
          const storedFlex = storedFlexValues[fieldKey];

          const processedItem = {
            ...item,
            flex:
              storedFlex !== undefined
                ? storedFlex
                : typeof item.flex === "string"
                ? parseInt(item.flex, 10) || 0
                : item.flex || 0,
            is_internal: Boolean(item.is_internal),
          };
          return processedItem;
        });
      } else {
        normalized[key] = [];
      }
    });
    return normalized;
  });

  const defaultItems = {
    head_view_fields: {
      field_name: "",
      display_name: "",
      is_internal: false,
      flex: 0,
    },
  };

  const handleBasicInfoChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleArrayFieldChange = (section, index, field, value) => {
    setFormData((prev) => {
      if (!prev[section] || !Array.isArray(prev[section])) return prev;

      const newArray = [...prev[section]];
      newArray[index] = {
        ...newArray[index],
        [field]: value,
      };

      return {
        ...prev,
        [section]: newArray,
      };
    });
  };

  const addNewItem = (section) => {
    setFormData((prev) => ({
      ...prev,
      [section]: [...prev[section], { ...defaultItems[section] }],
    }));
  };

  const removeItem = (section, index) => {
    setFormData((prev) => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSavingTransaction(true);
    try {
      const submitData = {
        ...formData,
      };

      const response = id
        ? await updateTransaction(id, submitData)
        : await createMasterTransaction(submitData);

      if (response && (response.id || response.success)) {
        const transactionId = response.id || id;
        if (transactionId && submitData.head_view_fields) {
          const flexValues = {};
          submitData.head_view_fields.forEach((field) => {
            const fieldKey = `${field.field_name}_${field.display_name}`;
            flexValues[fieldKey] = field.flex;
          });
          localStorage.setItem(
            `flex_values_${transactionId}`,
            JSON.stringify(flexValues)
          );
        }

        displayToast(
          `Master transaction ${id ? "updated" : "added"} successfully!`,
          "success"
        );
        navigate("/transactions");
      } else {
        displayToast(
          `Failed to ${id ? "update" : "add"} master transaction: ${
            response?.message || response?.description || "Unknown error"
          }`,
          "error"
        );
      }
    } catch (error) {
      console.error(
        `Error ${id ? "updating" : "adding"} master transaction:`,
        error
      );
      displayToast(
        `Error ${id ? "updating" : "adding"} master transaction: ${
          error.message || "Unknown error"
        }`,
        "error"
      );
    } finally {
      setIsSavingTransaction(false);
    }
  };

  return (
    <div className="transaction-page">
      <div className="transaction-container">
        <h1 className="page-header">
          {isViewMode ? "👁️ View Master Transaction" : (id ? "Edit Master Transaction" : "Master Transaction Configuration")}
        </h1>
        {isViewMode && (
          <div className="view-mode-banner">
            <span>📖</span> You are viewing this transaction in read-only mode
          </div>
        )}

        <form onSubmit={handleSubmit} className="transaction-form">
          <fieldset disabled={isViewMode} className="transaction-fieldset-reset">
          <section className="form-card">
            <div className="card-header">
              <h2>Basic Information</h2>
              <span className="badge">Required</span>
            </div>
            <div className="card-body">
              <div className="input-grid">
                <div className="input-group">
                  <label htmlFor="tran_code">Transaction Code</label>
                  <input
                    type="text"
                    id="tran_code"
                    name="tran_code"
                    value={formData.tran_code}
                    onChange={handleBasicInfoChange}
                    required
                    placeholder="Enter transaction code"
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="name">Transaction Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleBasicInfoChange}
                    required
                    placeholder="Enter transaction name"
                  />
                </div>
              </div>
              <div className="input-grid">
                <div className="input-group">
                  <label htmlFor="customer_type">Customer Type</label>
                  <select
                    id="customer_type"
                    name="customer_type"
                    value={formData.customer_type}
                    onChange={handleBasicInfoChange}
                    required
                  >
                    {CUSTOMER_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="input-group">
                  <label>Transaction Type</label>
                  <input
                    type="text"
                    value="Master"
                    disabled
                    className="disabled-input"
                  />
                </div>
              </div>
              <div className="checkbox-grid">
                <label className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    name="is_auto_transaction_no"
                    checked={formData.is_auto_transaction_no}
                    onChange={handleBasicInfoChange}
                  />
                  <span className="checkbox-label">
                    Auto Transaction Number
                  </span>
                </label>
                <label className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    name="is_back_dated_entry_allowed"
                    checked={formData.is_back_dated_entry_allowed}
                    onChange={handleBasicInfoChange}
                  />
                  <span className="checkbox-label">
                    Back Dated Entry Allowed
                  </span>
                </label>
                <label className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    name="is_pick_up_mandatory"
                    checked={formData.is_pick_up_mandatory}
                    onChange={handleBasicInfoChange}
                  />
                  <span className="checkbox-label">Pick Up Mandatory</span>
                </label>
              </div>
            </div>
          </section>

          <section className="form-card">
            <div className="card-header">
              <h2>Head View Fields</h2>
            </div>
            <div className="card-body">
              <div className="data-rows">
                {formData?.head_view_fields?.map((field, index) => {
                  return (
                    <div key={index} className="data-row">
                      <div className="row-inputs">
                        <input
                          type="text"
                          placeholder="Field Name"
                          value={field.field_name || ""}
                          onChange={(e) =>
                            handleArrayFieldChange(
                              "head_view_fields",
                              index,
                              "field_name",
                              e.target.value
                            )
                          }
                          required
                        />
                        <input
                          type="text"
                          placeholder="Display Name"
                          value={field.display_name || ""}
                          onChange={(e) =>
                            handleArrayFieldChange(
                              "head_view_fields",
                              index,
                              "display_name",
                              e.target.value
                            )
                          }
                          required
                        />
                        <input
                          type="number"
                          placeholder="Flex Value"
                          value={field.flex || ""}
                          onChange={(e) => {
                            handleArrayFieldChange(
                              "head_view_fields",
                              index,
                              "flex",
                              e.target.value === ""
                                ? 0
                                : parseInt(e.target.value, 10) || 0
                            );
                          }}
                          min="0"
                        />
                        <label className="checkbox-wrapper inline-checkbox">
                          <input
                            type="checkbox"
                            checked={field.is_internal || false}
                            onChange={(e) =>
                              handleArrayFieldChange(
                                "head_view_fields",
                                index,
                                "is_internal",
                                e.target.checked
                              )
                            }
                          />
                          <span className="checkbox-label">Internal Field</span>
                        </label>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem("head_view_fields", index)}
                        className="remove-btn"
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => addNewItem("head_view_fields")}
                className="add-btn"
              >
                ＋ Add Head View Field
              </button>
            </div>
          </section>
          </fieldset>

          <div className="form-actions">
            {isViewMode ? (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate("/transactions")}
                >
                  ← Back to List
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate(`/transactions/master/edit/${id}`, { state: { transaction: formData } })}
                >
                  ✏️ Edit Transaction
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate("/transactions")}
                >
                  ✕ Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {isSavingTransaction ? (
                    <span className="loader"></span>
                  ) : (
                    "✓ Save Master Transaction"
                  )}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default MasterTransaction;
